// src/pages/Community/StoryCharacters/CharacterDetailContent.jsx
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";   // ✅ ADD
import {
    LikeBar,
    ReactionBar,
    PresenceBadge,
    Discussion,
} from "../components";

function Section({ id, title, children }) {
    const headingId = title ? `${id}-h` : undefined;
    return (
        <section
            id={id}
            className="char-sec"
            {...(title
                ? { "aria-labelledby": headingId }
                : { "aria-label": id })}
        >
            {title && (
                <h2 className="char-h2" id={headingId}>
                    {title}
                </h2>
            )}
            {children}
        </section>
    );
}

export default function CharacterDetailContent({
    c,
    showTitle = true,
    basePath,
    selectedReaction,
    onSelectReaction,
    onOpenLightbox,
    yt,
    showDiscussion = false,
}) {
    if (!c) return null;

    return (
        <>
            {/* HERO */}
            <header className="char-hero-row">
                <div className="char-hero-left">
                    <div className="char-portrait-wrap">
                        {c.portrait ? (
                            <img
                                className="char-portrait"
                                src={c.portrait}
                                alt={`${c.name} portrait`}
                            />
                        ) : (
                            <div className="char-portrait char-portrait--placeholder">🎭</div>
                        )}
                    </div>
                </div>

                <div className="char-hero-right">

                    {/* Chips / meta */}
                    {(c.bio?.status || c.characterType || c.affiliation) && (
                        <div className="char-chips">
                            {c.bio?.status && (
                                <span className="char-chip">📌 {c.bio.status}</span>
                            )}
                            {c.characterType && (
                                <span className="char-chip">🎭 {c.characterType}</span>
                            )}
                            {c.affiliation && (
                                <span className="char-chip">🏷 {c.affiliation}</span>
                            )}
                        </div>
                    )}
                    {showTitle && (
                        <h1 className="char-detail-title">{c.name}</h1>
                    )}

                    {selectedReaction && (
                        <div className="msn-featured-reaction">
                            {selectedReaction}
                        </div>
                    )}

                    {c.desc && (
                        <div className="char-md char-prose">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {c.desc}
                            </ReactMarkdown>
                        </div>
                    )}
                    {/* Quick facts */}
                    <div className="char-facts-grid">
                        {c.bio?.fullName && (
                            <div className="char-fact">
                                <span>Full Name</span>
                                <strong>{c.bio.fullName}</strong>
                            </div>
                        )}
                        {c.affiliation && (
                            <div className="char-fact">
                                <span>Game</span>
                                <strong>{c.affiliation}</strong>
                            </div>
                        )}
                        {c.voiceActor && (
                            <div className="char-fact">
                                <span>Voice Actor</span>
                                <strong>{c.voiceActor}</strong>
                            </div>
                        )}

                        {c.bio?.born && (
                            <div className="char-fact">
                                <span>Born</span>
                                <strong>{c.bio.born}</strong>
                            </div>
                        )}
                        {c.age != null && (
                            <div className="char-fact">
                                <span>Age</span>
                                <strong>{c.age}</strong>
                            </div>
                        )}
                        {c.origin && (
                            <div className="char-fact">
                                <span>Origin</span>
                                <strong>{c.origin}</strong>
                            </div>
                        )}

                        {c.location && (
                            <div className="char-fact">
                                <span>Location</span>
                                <strong>{c.location}</strong>
                            </div>
                        )}
                        {c.occupation && (
                            <div className="char-fact">
                                <span>Occupation</span>
                                <strong>{c.occupation}</strong>
                            </div>
                        )}
                        {c.alias && (
                            <div className="char-fact">
                                <span>Alias</span>
                                <strong>{c.alias}</strong>
                            </div>
                        )}

                        {c.bio?.partner && (
                            <div className="char-fact">
                                <span>Partner</span>
                                <strong>{c.bio.partner}</strong>
                            </div>
                        )}
                        {c.bio?.children && (
                            <div className="char-fact">
                                <span>Children</span>
                                <strong>{c.bio.children}</strong>
                            </div>
                        )}
                        {c.bio?.affiliations?.length > 0 && (
                            <div className="char-fact">
                                <span>Affiliations</span>
                                <strong>{c.bio.affiliations.join(", ")}</strong>
                            </div>
                        )}
                    </div>
                </div>

            </header>

            {/* MAIN GRID */}
            <div>
                <main className="char-main">
                    {/* Biography */}
                    {(c.bio?.background ||
                        c.bio?.personality ||
                        c.bio?.skills ||
                        c.bio?.dreams ||
                        c.bio?.liking) && (
                            <Section id="biography" title="Biography">
                                {c.bio?.background && (
                                    <>
                                        <h3 className="char-h3">Background</h3>
                                        <div className="char-md char-prose">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {c.bio.background}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}

                                {c.bio?.personality && (
                                    <>
                                        <h3 className="char-h3">Personality</h3>
                                        <div className="char-md char-prose">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {c.bio.personality}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}

                                {c.bio?.skills && (
                                    <>
                                        <h3 className="char-h3">Skills</h3>
                                        <div className="char-md char-prose">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {c.bio.skills}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}

                                {c.bio?.dreams && (
                                    <>
                                        <h3 className="char-h3">Dreams</h3>
                                        <div className="char-md char-prose">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {c.bio.dreams}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}

                                {c.bio?.liking && (
                                    <>
                                        <h3 className="char-h3">Interests</h3>
                                        <div className="char-md char-prose">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {c.bio.liking}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}
                            </Section>
                        )}

                    {/* Story (long) + optional sub-sections */}
                    {(c.storyLong?.trim?.() ||
                        c.backstory?.trim?.() ||
                        c.arc?.trim?.() ||
                        c.fate?.trim?.()) && (
                            <Section id="story" title="Story">
                                {c.storyLong?.trim?.() && (
                                    <div className="char-md char-prose">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {c.storyLong}
                                        </ReactMarkdown>
                                    </div>
                                )}

                                {c.backstory?.trim?.() && (
                                    <>
                                        <h3 className="char-h3">Backstory</h3>
                                        <div className="char-md char-prose">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {c.backstory}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}

                                {c.arc?.trim?.() && (
                                    <>
                                        <h3 className="char-h3">Character Arc</h3>
                                        <div className="char-md char-prose">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {c.arc}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}

                                {c.fate?.trim?.() && (
                                    <>
                                        <h3 className="char-h3">Fate</h3>
                                        <div className="char-md char-prose">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {c.fate}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}
                            </Section>
                        )}

                    {/* Quotes */}
                    {Array.isArray(c.quotes) && c.quotes.length > 0 && (
                        <Section id="quotes" title="Notable Quotes">
                            <ul className="char-quote-list">
                                {c.quotes.map((q, i) => (
                                    <li key={i} className="char-quote">
                                        “{q}”
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    )}

                    {/* Relationships */}
                    {Array.isArray(c.relationships) && c.relationships.length > 0 && (
                        <Section id="relationships" title="Relationships">
                            <div className="char-rel">
                                {c.relationships.map((r, i) => (
                                    <div
                                        key={i}
                                        className="char-relchip"
                                        title={r.type || "Relationship"}
                                    >
                                        {r.characterId ? (
                                            <Link
                                                to={`/story-characters/${r.characterId}`}
                                            >
                                                {r.name}
                                            </Link>
                                        ) : (
                                            <span>{r.name}</span>
                                        )}
                                        {r.type && (
                                            <span className="char-reltype"> • {r.type}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* Gallery */}
                    {Array.isArray(c.gallery) && c.gallery.length > 0 && (
                        <Section id="gallery" title="Gallery">
                            <div className="char-gallery">
                                {c.gallery.map((src, i) => (
                                    <button
                                        key={i}
                                        className="char-shotbtn"
                                        onClick={() => onOpenLightbox(i)}
                                        aria-label={`Open image ${i + 1}`}
                                    >
                                        <img
                                            className="char-shot"
                                            src={src}
                                            alt={`Image ${i + 1}`}
                                            loading="lazy"
                                        />
                                    </button>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* Video */}
                    {c.videoUrl && (
                        <Section id="video" title="Video">
                            {yt ? (
                                <div className="char-video">
                                    <iframe
                                        title="Character video"
                                        src={yt}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        loading="lazy"
                                    />
                                </div>
                            ) : (
                                <a
                                    className="char-link"
                                    href={c.videoUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    ▶ Open video
                                </a>
                            )}
                        </Section>
                    )}

                    {/* ENGAGEMENT (moved below quotes) */}
                    {/* ENGAGEMENT BAR (INLINE, BELOW CONTENT) */}
                    <div className="char-engagement-row">
                        <PresenceBadge
                            presenceKey="story_characters"
                            entityId={c.id}
                        />

                        <LikeBar basePath={basePath} />

                        <ReactionBar
                            basePath={basePath}
                            visibleReactions={[
                                "love",
                                "fire",
                                "insightful",
                                "cool",
                                "funny",
                            ]}
                            onSelect={onSelectReaction}
                        />
                    </div>

                    <Section id="engagement" title="Community">
                        {showDiscussion && (
                            <Discussion basePath={basePath} />
                        )}
                    </Section>


                </main>
            </div>
        </>
    );
}
